import {
  APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { QRCodeModule } from 'angularx-qrcode';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MarkdownModule } from 'ngx-markdown';
import { FormsModule } from '@angular/forms';
import { MatPaginatorModule } from '@angular/material/paginator';
import { DatePipe, NgOptimizedImage, registerLocaleData } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import * as Sentry from '@sentry/angular';
import * as de from '@angular/common/locales/de';
import { ProductPlacementComponent } from '@components/product-placement/product-placement.component';
import { CardBarComponent } from '@components/card-bar/card-bar.component';
import { P404ExtComponent } from '@pages/error/p404.ext.component';
import { ForgotPasswordComponent } from '@pages/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from '@pages/reset-password/reset-password.component';
import { DashboardComponent } from '@pages/dashboard/dashboard.component';
import { SupportComponent } from '@pages/support/support.component';
import { TicketFormComponent } from '@pages/support/ticket-form.component';
import { EmailFormComponent } from '@pages/support/email-form.component';
import { VerifyEmailComponent } from '@pages/verify-email/verify-email.component';
import { VERSION } from '@environments/version';

import { MatInputModule } from '@angular/material/input';
import WebFont from 'webfontloader';
import { MultiSelectModule } from 'primeng/multiselect';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { P500ExtComponent } from '@pages/error/p500.ext.component';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ProductFruitsService } from '@services/productfruits.service';
import { KeycloakAngularModule } from 'keycloak-angular';
import {
  DeviceModule, JobModule, SoftwareModule, WorkflowModule,
} from '@nimmsta/ng-workflow-lib';
import {
  NimmstaCommonsModule, initQuill, NAV_ITEM_SERVICE,
  Environment,
  ServiceType,
} from '@nimmsta/ng-commons-lib';
import { environment } from '@environments/environment';
import { PagesModule } from '@nimmsta/ng-b2b-lib';
import { MatTreeModule } from '@angular/material/tree';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { SortableModule } from 'ngx-bootstrap/sortable';
import { WorkflowNavItemService } from '@services/workflownav.service';
import { LicensesComponent } from '@pages/licenses/licenses.component';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { QuillModule } from 'ngx-quill';
import { InformationComponent } from '@pages/information/information.component';
import { InformationItemComponent } from '@pages/information/information-item.component';
import { InformationFolderComponent } from '@pages/information/information-folder.component';
import { Router } from '@angular/router';
import { StartPageComponent } from '@pages/start-page/start-page.component';
import { RequestAccessComponent } from '@pages/request-access/request-access.component';
import { LayoutComponent } from './layout/layout.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

WebFont.load({
  custom: { families: ['Material Icons', 'Material Symbols Outlined'] },
});

initQuill();

@NgModule({
  declarations: [
    AppComponent,
    LayoutComponent,
    RequestAccessComponent,
    P404ExtComponent,
    P500ExtComponent,
    ProductPlacementComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    VerifyEmailComponent,
    DashboardComponent,
    CardBarComponent,
    SupportComponent,
    TicketFormComponent,
    EmailFormComponent,
    InformationComponent,
    InformationItemComponent,
    InformationFolderComponent,
    LicensesComponent,
    StartPageComponent,
  ],
  bootstrap: [AppComponent],
  imports: [AppRoutingModule,
    MatDividerModule,
    BrowserModule,
    BrowserAnimationsModule,
    ModalModule.forRoot(),
    TabsModule.forRoot(),
    BsDatepickerModule.forRoot(),
    QRCodeModule,
    MatProgressSpinnerModule,
    MarkdownModule.forRoot(),
    FormsModule,
    MatPaginatorModule,
    MatInputModule,
    NgOptimizedImage,
    MultiSelectModule,
    MonacoEditorModule.forRoot(),
    KeycloakAngularModule,
    MatTreeModule,
    MatIcon,
    MatIconButton,
    TypeaheadModule,
    SortableModule,
    AccordionModule.forRoot(),
    NimmstaCommonsModule.forRoot({
      environment: environment.environment as Environment | undefined,
      serviceType: ServiceType.CLOUD,
      apiUrls: environment.apiUrls,
      applicationUrls: environment.applicationUrls,
      defaultPageSizeOptions: environment.defaultPageSizeOptions,
      defaultPageSize: environment.defaultPageSize,
      USER_KEY: environment.USER_KEY,
      keycloakUrl: environment.keycloakUrl,
      keycloakRealm: environment.keycloakRealm,
      keycloakClientId: environment.keycloakClientId,
      defaultConfigurationVersion: environment.defaultConfigurationVersion,
      androidAppSoftwareId: environment.androidAppSoftwareId,
      translationFiles: [
        './assets/i18n/commons/',
        './assets/i18n/b2b/',
        './assets/i18n/workflow/',
        './assets/i18n/',
      ],
      // eslint-disable-next-line max-len
      maintenanceInfoSource: 'assets/maintenance/cloud-maintenance.json',
    }),
    WorkflowModule,
    JobModule,
    SoftwareModule,
    PagesModule,
    DeviceModule,
    QuillModule.forRoot(),
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'de' },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (service: ProductFruitsService) => () => service.serviceInit(),
      deps: [ProductFruitsService],
      multi: true,
    },
    DatePipe,
    {
      provide: NAV_ITEM_SERVICE,
      useClass: WorkflowNavItemService,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor() {
    registerLocaleData(de.default);

    Sentry.init({
      dsn: 'https://49eccb54d299cda1c1debb4ab18993b1@sentry.nimmsta.com/6',
      release: VERSION.fullHash,
      environment: (() => {
        switch (environment.environment) {
          case Environment.PROD: return 'production';
          case Environment.STAGING: return 'staging';
          case Environment.DEV: return 'development';
          default: return 'unknown';
        }
      })(),

      // PII
      sendDefaultPii: true,

      // Setup integrations
      integrations: [
        Sentry.browserTracingIntegration({}),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
        }),
        // ToDo: translation
        Sentry.feedbackIntegration({
          autoInject: false,
          colorScheme: 'light',
          showBranding: false,
          formTitle: 'Report a problem',
          messagePlaceholder: 'Please provide a description of the problem you encountered.',
          submitButtonLabel: 'Send feedback',
        }),
      ],
      beforeSend(event, hint) {
        // Filter out HTTP 0 errors
        if (
          hint?.originalException
          && typeof hint.originalException === 'string'
          && (hint.originalException as string).includes('0 Unknown Error')
        ) {
          return null;
        }

        return event;
      },

      // Tracing
      tracesSampleRate: environment.production ? 0.1 : 1.0,

      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        // 'localhost',
        /^https:\/\/backend.*\.nimmsta\.com/,
        /^https:\/\/backend.*\.nimmsta-test\.de/,
        /^https:\/\/api\.nimmsta\.com/,
        /^https:\/\/api\.nimmsta-test\.de/,
      ],

      // Session Replay
      replaysSessionSampleRate: environment.production ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,
    });
  }
}
